import type { ShopifyProduct } from "../api/ShopifyApi";
import type { ApiWishlist, Wishlist, WishlistItem } from "../api/WishlistApi";
import type { ProductInfo } from "./WishlistState";
import type { Product as StorefrontProduct } from "../api/StorefrontApi";

export function mapApiToWishlist(
  wishlist: ApiWishlist,
  products: StorefrontProduct[],
): Wishlist {
  const itemsWithProducts = wishlist.items
    .map((item) => {
      const product = products.find(
        (product) => product?.id.endsWith(item.productId.toString()),
      );

      if (!product) return null;

      return {
        id: item.id,
        variantId: item.variantId,
        product: {
          ...mapStorefrontProduct(product),
          hidden: false,
        },
        metafields: undefined,
      } as WishlistItem;
    })
    .filter((item) => !!item) as WishlistItem[];

  return {
    id: wishlist.isMine ? "mine" : wishlist.id,
    publicId: wishlist.publicId,
    isMine: wishlist.isMine,
    numItems: itemsWithProducts.length,
    items: itemsWithProducts,
  };
}

export function mapWishlistItemToProductInfo({
  wishlistItem,
  productHandle,
  variantId,
}: {
  wishlistItem?: WishlistItem;
  productHandle: string;
  variantId?: number;
}): ProductInfo {
  if (!wishlistItem) {
    return {
      productHandle,
      wishlistItemId: undefined,
      productId: 0,
      variantId: variantId,
      inWishlist: false,
    };
  }

  return {
    productHandle,
    wishlistItemId: wishlistItem.id,
    productId: wishlistItem.product.id,
    variantId: wishlistItem.variantId,
    inWishlist: true,
  };
}

export const mapStorefrontProduct = (
  input: StorefrontProduct,
): ShopifyProduct => {
  return {
    id: parseInt(input.id.split("/").pop() as string),
    available: input.availableForSale,
    compare_at_price_max:
      parseFloat(input.compareAtPriceRange.maxVariantPrice.amount) * 100,
    compare_at_price_min:
      parseFloat(input.compareAtPriceRange.minVariantPrice.amount) * 100,
    compare_at_price_varies:
      input.compareAtPriceRange.maxVariantPrice.amount !==
      input.compareAtPriceRange.minVariantPrice.amount,
    featured_image: input.featuredImage?.url ?? null,
    handle: input.handle,
    images: input.images.nodes.map((image) => image.url),
    options: input.options,
    price_max: parseFloat(input.priceRange.maxVariantPrice.amount) * 100,
    price_min: parseFloat(input.priceRange.minVariantPrice.amount) * 100,
    price_varies:
      input.priceRange.maxVariantPrice.amount !==
      input.priceRange.minVariantPrice.amount,
    tags: input.tags,
    title: input.title,
    type: input.productType,
    url: `${window.Shopify.routes.root}products/${input.handle}`,
    variants: input.variants.nodes.map((variant) => ({
      available: variant.availableForSale,
      currentlyNotInStock: variant.currentlyNotInStock,
      compare_at_price: variant.compareAtPrice
        ? parseFloat(variant.compareAtPrice.amount) * 100
        : null,
      featured_image: variant.image
        ? { alt: variant.image.altText, src: variant.image.url }
        : null,
      id: parseInt(variant.id.split("/").pop() as string),
      option1: variant.selectedOptions[0].value,
      option2: variant.selectedOptions[1]?.value ?? null,
      option3: variant.selectedOptions[2]?.value ?? null,
      price: parseFloat(variant.price.amount) * 100,
      sku: variant.sku,
      title: variant.title,
      metafields:
        variant.metafields?.reduce(
          (metafields, metafield) => {
            if (metafield?.value) {
              return {
                ...metafields,
                [metafield.namespace]: {
                  ...(metafields[metafield.namespace] ?? {}),
                  [metafield.key]: metafield.value,
                },
              };
            }
            return metafields;
          },
          {} as Record<string, Record<string, string>>,
        ) ?? {},
    })),
    vendor: input.vendor,
    metafields:
      input.metafields?.reduce(
        (metafields, metafield) => {
          if (metafield?.value) {
            return {
              ...metafields,
              [metafield.namespace]: {
                ...(metafields[metafield.namespace] ?? {}),
                [metafield.key]: metafield.value,
              },
            };
          }
          return metafields;
        },
        {} as Record<string, Record<string, string>>,
      ) ?? {},
  };
};

// export function findProductVariant(
//   product: ShopifyProduct,
//   variantId?: number
// ) {
//   return product.variants.find((variant) => variant.id === variantId);
// }

export function findWishlistItem({
  wishlist,
  productHandle,
  productId,
  variantId,
  mulitVariantMode,
}: {
  wishlist: Wishlist;
  productHandle?: string;
  productId?: number;
  variantId?: number;
  mulitVariantMode: boolean;
}) {
  return wishlist.items.find((item) => {
    const isProductId = productId && item.product.id === productId;

    const isProductHandle =
      productHandle && item.product.handle === productHandle;

    return (
      (isProductId || isProductHandle) &&
      (!variantId || !mulitVariantMode || item.variantId === variantId)
    );
  });
}
